import { ApiError, get, post, patch, del, type Me } from '../api.js';
import {
  button, chip, clear, drawer, el, empty, field, input, mono, panel, row, select, skeleton,
  table, toast,
} from '../ui.js';
import { encodeQr, qrSvgPath } from '../../../../packages/core/src/qr.js';
import { STATES } from '../../../../packages/core/src/states.js';
import {
  ORG_ROLES, ORG_ROLE_LABEL, ORG_ROLE_BLURB, type OrgRole,
} from '../../../../packages/core/src/rbac.js';
import { appointmentsPanel, carriersPanel } from './cases.js';
import { productsPanel } from './products.js';
import { commissionPanel } from './commission.js';

/** Every place a producer can be licensed, by whole name, in name order. */
const PLACE_OPTIONS: Array<[string, string]> = [...STATES]
  .sort((a, b) => a.name.localeCompare(b.name, 'en'))
  .map((s) => [s.code, s.name]);

interface ScoringRule { id: string; label: string; points: number; enabled: boolean; kind: string }
interface Config {
  scoring: { name: string; base: number; min: number; max: number; enabled: boolean; rules: ScoringRule[] };
  compliance: Record<string, number | boolean>;
  ai: { enabled?: boolean };
}

/**
 * `reload` rather than `go(<the tab you are already on>)`.
 *
 * The account is fetched once and cached for the life of the page, so
 * re-rendering the same tab redraws it from the copy of `me` that was true
 * before the button was pressed. Every switch on this page reported the
 * opposite of what it had just done until the next full page load — the
 * multi-factor one had done it since it was written. `reload` drops the cached
 * account and renders again, which is the only thing that makes a panel showing
 * server state tell the truth right after changing it.
 */
export function settingsView(me: Me, tab: string, go: (p: string) => void, reload: () => void): HTMLElement {
  const host = el('div');
  const tabs: Array<[string, string, boolean]> = [
    ['security', 'Your security', true],
    ['agency', 'Agency', me.capabilities.includes('org.manage')],
    ['compliance', 'Calling rules', me.capabilities.includes('config.compliance')],
    ['scoring', 'Lead scoring', me.capabilities.includes('config.scoring')],
    ['sources', 'Lead sources', me.capabilities.includes('lead.import')],
    ['carriers', 'Carriers', true],
    ['products', 'Products & rates', true],
    // Everybody sees it. A rate is not a secret from the person it pays.
    ['pay', 'What cases pay', true],
    ['people', 'People', me.capabilities.includes('user.invite')],
    ['ai', 'Assistant', me.capabilities.includes('ai.use')],
    ['audit', 'Audit trail', me.capabilities.includes('audit.read')],
  ];
  const visible = tabs.filter(([, , ok]) => ok);
  const current = visible.some(([k]) => k === tab) ? tab : visible[0]![0];

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Settings' }),
    el('h1', { text: 'Settings' })));

  const bar = el('div', { class: 'tabs', role: 'tablist' });
  for (const [key, label] of visible) {
    bar.append(el('button', {
      // Explicit, because a bare <button> defaults to type="submit". These tabs
      // navigate; the day one of them ends up inside a form, the default would
      // submit it.
      type: 'button',
      role: 'tab', 'aria-selected': key === current ? 'true' : 'false',
      onclick: () => go(`/settings/${key}`),
    }, label));
  }
  host.append(bar);

  const body = el('div');
  host.append(body);

  switch (current) {
    case 'security': void security(); break;
    case 'agency': void agency(); break;
    case 'compliance': void compliance(); break;
    case 'scoring': void scoring(); break;
    case 'sources': void sourcesTab(); break;
    case 'carriers':
      body.append(panel('Carriers you write', carriersPanel(me)));
      // An appointment is not a licence and is not implied by one. It belongs
      // beside the carrier list because that is the list it points at, and
      // because the writing number a commission is paid against otherwise
      // lives in somebody's inbox until they leave the agency.
      body.append(panel('Your carrier appointments', appointmentsPanel(me)));
      break;
    case 'products': body.append(productsPanel(me)); break;
    case 'pay': body.append(panel('What cases pay', commissionPanel(me))); break;
    case 'people': void people(); break;
    case 'ai': void ai(); break;
    case 'audit': void audit(); break;
    default: break;
  }

  /* ------------------------------------------------------------ security -- */
  async function security(): Promise<void> {
    clear(body);
    const factors: HTMLElement[] = [];
    if (me.user.mfaEnabled) {
      const on: string[] = [];
      if (me.user.mfaTotp) on.push('an authenticator app');
      if (me.user.mfaPhone) on.push('a code texted to your phone');
      factors.push(
        el('div', { class: 'okbox' },
          el('b', null, 'On. '),
          `Signing in needs ${on.length ? on.join(' or ') : 'a second factor'}.`
          + (me.user.mfaRecoveryLeft
            ? ` ${me.user.mfaRecoveryLeft} recovery code${me.user.mfaRecoveryLeft === 1 ? '' : 's'} left.`
            : ' No recovery codes left — that is your only way back in if you lose your phone.')),
        el('div', { style: 'margin-top:12px' }, button('Turn it off', 'danger', async () => {
          if (!confirm('Turn off multi-factor? This account can open other people\'s personal and financial data.')) return;
          await del('/v1/me/mfa');
          toast('Multi-factor turned off.', 'ok');
          reload();
        })));
    } else {
      factors.push(mfaSetup());
    }
    body.append(panel('Multi-factor authentication', ...factors));
    body.append(panel('Code by text message', phoneFactor()));
    if (me.capabilities.includes('org.manage')) body.append(panel('Agency policy', mfaPolicy()));

    const cur = input({ type: 'password', autocomplete: 'current-password' });
    const next = input({ type: 'password', autocomplete: 'new-password', minlength: '12' });
    const save = button('Change password', 'pri');
    save.addEventListener('click', async () => {
      save.disabled = true;
      try {
        await post('/v1/me/password', { current: cur.value, next: next.value });
        toast('Password changed. Every other device has been signed out.', 'ok');
        cur.value = ''; next.value = '';
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not change it.', 'crit'); } finally { save.disabled = false; }
    });
    body.append(panel('Password',
      field('Current password', cur),
      field('New password', next, 'At least 12 characters. Changing it signs out every other device.'),
      save));
  }

  /**
   * The agency-wide multi-factor requirement.
   *
   * An agency's duty to protect the records it holds does not decompose into
   * a dozen individual choices, so an owner needs to be able to require this
   * of everybody rather than hope for it. Turning it on funnels anybody
   * without a factor to this page and blocks nothing else about their account.
   */
  function mfaPolicy(): HTMLElement {
    const wrap = el('div');
    const on = Boolean(me.org?.requireMfa);

    const status = el('div', { class: on ? 'okbox' : 'warnbox' },
      el('b', null, on ? 'Required. ' : 'Not required. '),
      on
        ? 'Nobody at this agency can work without a second factor.'
        : 'Each person decides for themselves, which means some of them will not.');

    const count = el('p', { class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:12px 0' });
    const toggle = button(on ? 'Stop requiring it' : 'Require it for everyone', on ? 'danger' : 'pri');

    toggle.addEventListener('click', async () => {
      if (!on && !confirm(
        'Require multi-factor for everyone at this agency?\n\n'
        + 'Anyone who has not set it up will be sent to this page and cannot work until they do. '
        + 'You can turn this off again at any time.',
      )) return;
      toggle.disabled = true;
      try {
        const out = await post<{ requireMfa: boolean; enrolled: number; notEnrolled: number }>(
          '/v1/org/security', { requireMfa: !on },
        );
        toast(out.requireMfa
          ? `Required. ${out.notEnrolled} ${out.notEnrolled === 1 ? 'person needs' : 'people need'} to set it up.`
          : 'No longer required.', 'ok');
        reload();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not change it.', 'crit');
        toggle.disabled = false;
      }
    });

    wrap.append(
      status,
      el('p', { class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:12px 0' },
        'This account and every other one here can open other people\u2019s dates of birth, '
        + 'addresses and bank details. A stolen password should not be enough to reach any of it.'),
      count,
      toggle);
    return wrap;
  }

  /**
   * A second factor by text message.
   *
   * Offered after the authenticator app, not instead of it, and the copy says
   * why: a text can be diverted by someone who talks a mobile carrier into
   * moving the number. It is here because field staff will actually use it,
   * and a factor people use beats a stronger one they do not.
   */
  function phoneFactor(): HTMLElement {
    const wrap = el('div');

    const explain = el('p', { class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:12px' },
      'A six-digit code sent to your mobile. Useful when you are working from a phone and have no authenticator app to hand. ',
      el('b', null, 'An authenticator app is the stronger of the two'),
      ' — a text can be redirected by someone who persuades your carrier to move your number, so use this as a convenience or a backup rather than your only protection.');

    if (me.user.mfaPhone) {
      wrap.append(
        el('div', { class: 'okbox' }, el('b', null, 'Confirmed. '), 'Codes can be sent to the number on file.'),
        explain,
        button('Remove this number', 'danger', async () => {
          try {
            await del('/v1/me/mfa/phone');
            toast('Phone removed from multi-factor.', 'ok');
            reload();
          } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not remove it.', 'crit'); }
        }));
      return wrap;
    }

    const phone = input({ type: 'tel', inputmode: 'tel', autocomplete: 'tel', placeholder: 'Ten digits' });
    const send = button('Send me a code', 'pri');
    const stage = el('div');

    send.addEventListener('click', async () => {
      send.disabled = true;
      try {
        const out = await post<{ hint: string; devCode?: string }>('/v1/me/mfa/phone/begin', { phone: phone.value });
        clear(stage);
        const code = input({ inputmode: 'numeric', maxlength: '6', placeholder: '000000', autocomplete: 'one-time-code' });
        const confirmBtn = button('Confirm this number', 'pri');
        stage.append(
          el('p', { class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:12px 0 8px' },
            `Sent to ${out.hint}. It expires in five minutes.`),
          field('Code from the message', code),
          confirmBtn);
        if (out.devCode) toast(`Development build — the code is ${out.devCode}`, 'info');
        code.focus();
        confirmBtn.addEventListener('click', async () => {
          confirmBtn.disabled = true;
          try {
            const done = await post<{ recoveryCodes: string[] }>('/v1/me/mfa/phone/confirm', { code: code.value });
            clear(stage);
            stage.append(el('div', { class: 'okbox' }, el('b', null, 'Confirmed. '), 'Codes will be sent to this number when you sign in.'));
            if (done.recoveryCodes.length) stage.append(recoveryPanel(done.recoveryCodes));
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'That code was not right.', 'crit');
            confirmBtn.disabled = false;
          }
        });
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not send a code.', 'crit');
      } finally { send.disabled = false; }
    });

    if (!me.smsAvailable) {
      // Do not offer what the server cannot do. Without a provider configured,
      // this form takes a number, sends nothing, and reports a 503 — which
      // reads as a fault rather than as a setting nobody has turned on yet.
      wrap.append(
        explain,
        el('div', { class: 'note' },
          el('b', null, 'Text codes are not available on this server yet. '),
          'No text-message provider has been connected, so Cellvora will not accept a '
          + 'number it cannot actually send to — an account that looks protected and is '
          + 'not is worse than one that plainly is not.',
          el('br', null), el('br', null),
          'Use the authenticator app above instead. It works now, and it is the stronger '
          + 'of the two. An administrator can connect a provider later and this section '
          + 'will start working on its own.'));
      return wrap;
    }

    wrap.append(explain, field('Mobile number', phone, 'US mobile numbers only. This is used for sign-in codes, not for lead calls.'), send, stage);
    return wrap;
  }

  /** Recovery codes are shown exactly once, so the panel says so plainly. */
  function recoveryPanel(codes: string[]): HTMLElement {
    return el('div', null,
      el('p', { class: 's', style: 'font-size:12.5px;margin:14px 0 8px' },
        el('b', null, 'Write these down now. '),
        'Each works once, they are not shown again, and they are the only way back in if you lose both your phone and your authenticator app.'),
      el('div', { class: 'mono', style: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px' },
        ...codes.map((c) => el('div', { style: 'padding:8px;background:var(--hull-3);border:1px solid var(--rim);border-radius:var(--r-sm);text-align:center', text: c }))));
  }

  /**
   * The enrolment QR, drawn here rather than fetched.
   *
   * The `otpauth://` URL encoded below contains the shared secret for this
   * person's second factor. Sending it to an image service to be rendered
   * would put every account's multi-factor seed in somebody else's request
   * log, so it is drawn locally from `packages/core/src/qr.ts`.
   *
   * The typed key stays on the page underneath. A camera fails for all sorts
   * of ordinary reasons - a cracked lens, a desktop with no camera, an app
   * that only takes keys - and enrolment is the one step that must not have a
   * dead end in it.
   */
  function qrPanel(otpauthUrl: string, secret: string): HTMLElement {
    const box = el('div', { style: 'margin-bottom:14px' });

    try {
      const code = encodeQr(otpauthUrl);

      // The pixel size is an integer multiple of the module count, never a
      // round number of pixels. A QR is a sampling grid: a scanner locates the
      // finders, divides by the module count and reads the centre of each cell.
      // At a fractional scale — 188px across 57 modules is 3.298px each — the
      // renderer snaps some modules to three pixels and some to four, the grid
      // stops being uniform, and the sample points drift off the cells they are
      // meant to be reading. The code looks perfectly fine to a person and is
      // unreadable to a phone.
      //
      // Six pixels per module, floored to a whole number, keeps every cell
      // identical and lands between 250 and 350 pixels for every version this
      // encoder produces, small enough that a narrow phone never has to rescale it.
      const px = code.size * 5;
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', `0 0 ${code.size} ${code.size}`);
      svg.setAttribute('width', String(px));
      svg.setAttribute('height', String(px));
      svg.setAttribute('style', 'max-width:100%;height:auto');
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', 'Enrolment QR code for your authenticator app');
      // White underneath, always - a dark-theme page behind a QR is unscannable,
      // and the quiet zone is part of the symbol, not decoration.
      const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bg.setAttribute('width', String(code.size));
      bg.setAttribute('height', String(code.size));
      bg.setAttribute('fill', '#ffffff');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', qrSvgPath(code));
      path.setAttribute('fill', '#000000');
      svg.append(bg, path);
      box.append(el('div', {
        style: 'display:flex;justify-content:center;padding:14px;background:#fff;'
          + 'border:1px solid var(--rim);border-radius:var(--r-sm)',
      }, svg));
    } catch {
      // A QR that cannot be built is not a reason to block enrolment.
    }

    const reveal = el('details', { style: 'margin-top:10px' });
    reveal.append(
      el('summary', {
        style: 'cursor:pointer;font-size:12.5px;color:var(--ink-3)',
        text: 'Cannot scan it? Type this key in instead',
      }),
      el('div', {
        class: 'mono',
        style: 'padding:11px;margin-top:8px;background:var(--hull-3);border-radius:var(--r-sm);'
          + 'border:1px solid var(--rim);word-break:break-all;font-size:12.5px;'
          + 'user-select:all;-webkit-user-select:all',
        text: secret,
      }));
    box.append(reveal);
    return box;
  }

  function mfaSetup(): HTMLElement {
    const wrap = el('div');
    const start = button('Set up multi-factor', 'pri', async () => {
      try {
        const res = await post<{ secret: string; otpauthUrl: string }>('/v1/me/mfa/begin');
        clear(wrap);
        const code = input({ inputmode: 'numeric', maxlength: '6', placeholder: '000000' });
        const confirm = button('Confirm', 'pri');
        wrap.append(
          el('p', { style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:12px', text: 'Open your authenticator app, scan this, then type the six-digit code it shows.' }),
          qrPanel(res.otpauthUrl, res.secret),
          field('Code from the app', code),
          confirm);
        confirm.addEventListener('click', async () => {
          confirm.disabled = true;
          try {
            const out = await post<{ recoveryCodes: string[] }>('/v1/me/mfa/confirm', { code: code.value });
            clear(wrap);
            wrap.append(
              el('div', { class: 'okbox' }, el('b', null, 'Multi-factor is on. '), 'Save these recovery codes somewhere safe — each works once, and they are the only way back in if you lose your phone.'),
              el('div', { class: 'mono', style: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px;margin-top:12px' },
                ...out.recoveryCodes.map((c) => el('div', { style: 'padding:8px;background:var(--hull-3);border:1px solid var(--rim);border-radius:var(--r-sm);text-align:center', text: c }))));
          } catch (e) { toast(e instanceof ApiError ? e.message : 'That code was not right.', 'crit'); confirm.disabled = false; }
        });
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not start setup.', 'crit'); }
    });
    wrap.append(
      el('p', { style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:12px', text: 'This account can open dates of birth and bank details. Multi-factor is the difference between a stolen password and a stolen book of business.' }),
      start);
    return wrap;
  }

  /* -------------------------------------------------------------- agency -- */
  async function agency(): Promise<void> {
    clear(body);
    const name = input({ value: me.org?.name ?? '' });
    const npn = input({ placeholder: 'Digits only' });
    const save = button('Save', 'pri');
    save.addEventListener('click', async () => {
      save.disabled = true;
      try {
        await post('/v1/onboarding/agency', { name: name.value, npn: npn.value || undefined });
        toast('Saved.', 'ok');
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not save.', 'crit'); } finally { save.disabled = false; }
    });
    body.append(panel('Agency details',
      field('Agency name', name),
      field('NPN', npn, 'Your agency\'s National Producer Number.'),
      save));
    body.append(panel('Who sees which leads', leadVisibility()));
    body.append(intakeLinkPanel());
  }

  /**
   * Whether everybody here works one shared book, or only their own.
   *
   * Shipped ON, and the reason is a real morning: an agency imported its whole
   * book, none of it assigned to anybody yet, and every agent opened Leads to
   * an empty screen. That was the strict rule working exactly as written, and
   * it is indistinguishable from an import that silently failed — so the first
   * thing anyone did was try to import the file again.
   *
   * The switch exists because both answers are right for different shops. A
   * small agency shares one pool. One with producers who buy their own leads
   * does not, and quietly showing a producer's book to the desk beside them
   * would be a serious thing to do by default.
   *
   * What it never touches is another agency. That boundary is in the database,
   * not here, and nothing on this page can move it.
   */
  function leadVisibility(): HTMLElement {
    const wrap = el('div');
    const on = me.org?.sharedLeads !== false;

    const status = el('div', { class: 'okbox' },
      el('b', null, on ? 'Everyone sees every lead. ' : 'Each person sees their own. '),
      on
        ? 'Anyone with an account here can open any lead here, whether or not it is assigned to them.'
        : 'Agents see the leads assigned to them. A team leader also sees their team’s, '
          + 'and their team’s unclaimed ones. Owners and administrators see everything.');

    const toggle = button(
      on ? 'Limit each person to their own leads' : 'Let everyone see every lead',
      on ? 'danger' : 'pri',
    );
    toggle.addEventListener('click', async () => {
      if (on && !confirm(
        'Limit each person to their own leads?\n\n'
        + 'Anyone whose leads are not assigned to them will stop seeing them — including '
        + 'everything that has been imported and not yet handed out. You can turn this back on at any time.',
      )) return;
      toggle.disabled = true;
      try {
        await post<{ sharedLeads: boolean }>('/v1/org/leads', { sharedLeads: !on });
        toast(on ? 'Each person now sees only their own leads.' : 'Everyone here can now see every lead.', 'ok');
        reload();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not change it.', 'crit');
        toggle.disabled = false;
      }
    });

    wrap.append(
      status,
      el('p', { class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:12px 0' },
        'This only ever covers this agency. Nothing here shows your leads to anybody outside it.'),
      toggle);
    return wrap;
  }

  /**
   * The onboarding link.
   *
   * The alternative to this panel is a phone call: someone reads out a licence
   * table while someone else retypes it. That goes wrong in the one place it
   * must not — a mistyped licence number is a producer who cannot legally be
   * routed a lead in that state, and nothing in the system knows it is wrong.
   *
   * So: press a button, send the link, the agency fills it in themselves, and
   * what they typed is what lands in their record.
   */
  function intakeLinkPanel(): HTMLElement {
    const out = el('div');
    const make = button('Make an onboarding link', 'pri');
    // Filled in by `loadStatus`. Onboarding shuts itself the moment a form is
    // submitted, and a panel that only offers a button cannot say whether
    // pressing it produces a working link or a refusal.
    const state = el('div', { style: 'margin-bottom:14px' });

    /**
     * Reads back whether onboarding is open, and offers to reopen it.
     *
     * Closed is the ordinary end state, not a fault: the link closes itself on
     * the first successful submission, which is what stops a forwarded text
     * message from staying a way into this agency for a fortnight.
     *
     * Reopening undoes exactly that, so the copy says so before the press
     * rather than after it.
     */
    const loadStatus = async (): Promise<void> => {
      clear(state);
      try {
        const s = await get<{ closedAt: string | null }>('/v1/org/intake-status');

        if (!s.closedAt) {
          make.disabled = false;
          state.append(el('div', { class: 'okbox' },
            el('b', null, 'Onboarding is open. '),
            'A link made now works until it expires. The first time somebody submits '
            + 'the form, onboarding closes itself and every link for this agency stops '
            + 'working — including the copies sitting in whatever inbox you sent them to.'));
          return;
        }

        const closed = new Date(s.closedAt);
        const when = Number.isNaN(closed.getTime())
          ? null
          : closed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

        const reopen = button('Let onboarding links work again', 'danger');
        reopen.addEventListener('click', async () => {
          if (!confirm(
            'Reopen onboarding for this agency?\n\n'
            + 'Every onboarding link for this agency still inside its fourteen days starts '
            + 'working again — including copies you have already sent and cannot get back. '
            + 'Anyone holding one can add people, licences, lead sources and do-not-call '
            + 'entries to this agency without an account.\n\n'
            + 'It closes itself again the next time the form is submitted.',
          )) return;
          reopen.disabled = true;
          try {
            await post<{ ok: true }>('/v1/org/intake-reopen', {});
            toast('Onboarding reopened. Links work again until the form is submitted.', 'ok');
            // Read the state back rather than assume the press did what it looks
            // like it did. What this panel shows comes from `/v1/org/intake-status`
            // and not from the cached account, so re-fetching that — not `reload`,
            // which would drop any link already on screen — is what makes it true.
            await loadStatus();
          } catch (e) {
            toast(e instanceof ApiError ? e.message : 'Could not reopen onboarding.', 'crit');
            reopen.disabled = false;
          }
        });

        // Nothing to press until it is reopened: the server refuses to issue a
        // link into a closed agency, and a button whose only outcome is an error
        // is worse than one that is plainly unavailable.
        make.disabled = true;
        state.append(
          el('div', { class: 'note' },
            el('b', null, when ? `Onboarding closed on ${when}. ` : 'Onboarding is closed. '),
            'It closed itself when the form was submitted, so no link for this agency '
            + 'works and a new one cannot be made until it is reopened.'),
          el('p', { class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:12px 0' },
            'Reopening starts every link for this agency working again — not only a new '
            + 'one, but every copy already sent that is still inside its fourteen days. '
            + 'Anyone holding one can change this agency\'s name and time zone, and add '
            + 'people, licences, lead sources and do-not-call entries, without an account.'),
          el('p', { class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:12px 0' },
            'Anybody added that way arrives with no password and has to be invited before '
            + 'they can sign in. Onboarding closes itself again on the next submission, and '
            + 'who reopened it is recorded in the audit trail.'),
          reopen);
      } catch (e) {
        // Said out loud rather than left blank: without this the panel would
        // offer a link the server may well refuse, and nobody would know why.
        toast(e instanceof ApiError ? e.message : 'Could not read whether onboarding is open.', 'crit');
        state.append(el('div', { class: 'warnbox' },
          'Could not read whether onboarding is open. Making a link below still works if it is.'));
      }
    };

    make.addEventListener('click', async () => {
      make.disabled = true;
      make.textContent = 'Making it…';
      try {
        const link = await post<{ url: string; expiresAt: string }>('/v1/org/intake-link');
        show(link.url, new Date(link.expiresAt));
        make.textContent = 'Make another link';
        make.disabled = false;
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not make a link.', 'crit');
        make.textContent = 'Make an onboarding link';
        make.disabled = false;
        // Somebody submitted the form while this page sat open. Read the state
        // back, so the panel stops offering what the server has started
        // refusing. After the re-enable above, never before it.
        if (e instanceof ApiError && e.code === 'intake_closed') await loadStatus();
      }
    });

    function show(url: string, expires: Date): void {
      clear(out);
      // Read-only rather than disabled: a disabled input cannot be selected, and
      // selecting the text by hand is the fallback when the clipboard is not
      // available (an insecure origin, or a browser that refuses it).
      const box = input({ value: url, readonly: true, spellcheck: 'false' });
      // Set on the element rather than by adding `.mono`: `.inp` and `.mono` are
      // both single-class selectors, so the one declared later in the stylesheet
      // wins, and that is `.inp`. An inline style is the only one that beats it.
      box.style.fontFamily = 'var(--f-mono)';
      box.style.fontSize = '12px';
      box.addEventListener('focus', () => box.select());

      const copy = button('Copy link', '', async () => {
        try {
          await navigator.clipboard.writeText(url);
          toast('Link copied. Send it to the agency.', 'ok');
        } catch {
          box.focus();
          box.select();
          toast('Copying was blocked — the link is selected, press Ctrl+C (⌘C on a Mac).', 'info');
        }
      });

      out.append(
        el('div', { class: 'okbox', style: 'margin-bottom:12px' },
          el('b', null, 'Send this link to the agency. '),
          'It works until '
          + expires.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          + '. Anyone who has it can fill in the form, so send it to the owner directly '
          + 'rather than posting it anywhere.'),
        box,
        el('div', { style: 'display:flex;gap:8px;margin-top:10px;flex-wrap:wrap' },
          copy,
          button('Open it', 'ghost', () => window.open(url, '_blank', 'noopener'))),
      );
    }

    const wrap = panel('Onboarding link',
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:6px',
        text: 'One link, sent once. The agency fills in their own details, their '
          + 'people, their licences, where their leads come from, and anyone who has '
          + 'already asked not to be called. It lands straight in this agency\'s '
          + 'record — nobody retypes anything.',
      }),
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:14px',
        text: 'Whoever fills it in cannot sign in, cannot see a single lead, and '
          + 'cannot reach any other agency. The form only writes.',
      }),
      state,
      make,
      out);

    // Asked only on behalf of somebody who can act on the answer. The route
    // answers 403 to anybody else, and an ordinary agent should never be shown
    // an error for a question they did not ask. The Agency tab is gated on the
    // same capability — this checks again rather than trusting that from here.
    if (me.capabilities.includes('org.manage')) void loadStatus();
    return wrap;
  }

  /* ---------------------------------------------------------- compliance -- */
  async function compliance(): Promise<void> {
    clear(body);
    body.append(skeleton(4));
    const cfg = await get<Config>('/v1/config').catch(() => null);
    clear(body);
    if (!cfg) { body.append(el('div', { class: 'warnbox' }, 'Could not load the settings.')); return; }
    const c = cfg.compliance as Record<string, number | boolean>;

    const open = input({ type: 'number', min: '0', max: '23', value: String(c.federalOpen ?? 8) });
    const close = input({ type: 'number', min: '1', max: '24', value: String(c.federalClose ?? 21) });
    const scrub = input({ type: 'number', min: '1', max: '90', value: String(c.scrubMaxAgeDays ?? 31) });
    const cap = input({ type: 'number', min: '1', max: '10', value: String(c.weeklyAttemptCap ?? 3) });
    const stricter = el('input', { type: 'checkbox', checked: c.stricterOfState !== false ? 'true' : null });
    const consent = el('input', { type: 'checkbox', checked: c.requireConsent !== false ? 'true' : null });
    const save = button('Save the calling rules', 'pri');

    save.addEventListener('click', async () => {
      save.disabled = true;
      try {
        await post('/v1/onboarding/compliance', {
          federalOpen: Number(open.value),
          federalClose: Number(close.value),
          stricterOfState: (stricter as HTMLInputElement).checked,
          requireConsent: (consent as HTMLInputElement).checked,
          scrubMaxAgeDays: Number(scrub.value),
          weeklyAttemptCap: Number(cap.value),
        });
        toast('Calling rules saved.', 'ok');
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not save.', 'crit'); } finally { save.disabled = false; }
    });

    body.append(panel('Calling rules',
      el('div', { class: 'warnbox', style: 'margin-bottom:14px' },
        el('b', null, 'These are your agency\'s configured rules, not legal advice. '),
        'Review them with compliance counsel before this platform dials a real number.'),
      el('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:10px' },
        field('Calling opens at', open, '24-hour clock, in the lead\'s own time zone.'),
        field('Calling closes at', close)),
      field('Scrub must be no older than', scrub, 'Days. A list scrubbed longer ago than this cannot be dialled.'),
      field('Attempts allowed per number per week', cap),
      el('label', { class: 'field' }, el('span', { text: 'Use the tighter state rule' }),
        el('div', { style: 'display:flex;gap:9px;align-items:center;height:38px' }, stricter,
          el('span', { style: 'font-size:12.5px;color:var(--ink-3)', text: 'Michigan opens at 9am, Florida closes at 8pm. With this on, the tighter of federal and state wins — and with it off, this agency would be calling Michigan numbers an hour before Michigan allows it.' }))),
      el('label', { class: 'field' }, el('span', { text: 'Require a consent artifact' }),
        el('div', { style: 'display:flex;gap:9px;align-items:center;height:38px' }, consent,
          el('span', { style: 'font-size:12.5px;color:var(--ink-3)', text: 'With this on, a number with no stored consent is never dialled.' }))),
      save));

    const reg = el('div');
    body.append(reg);
    void registry(reg);
  }

  /* ---------------------------------------------------- do-not-call files -- */

  interface RegistryStatus {
    loads: Array<{ areaCode: string; source: string; numbers: number; loadedAt: string; coverage: string }>;
    book: Array<{ areaCode: string; leads: number; coverage: string }>;
    maxAgeDays: number;
    unreadable: number;
    byState: Array<{
      state: string; name: string; leads: number;
      artificialVoice: 'permitted' | 'prohibited' | 'unclear';
      voiceNote: string | null;
    }>;
    statelessLeads: number;
  }

  /**
   * Which registry files this agency actually has to buy — and which are free.
   *
   * The FTC sells the do-not-call registry per area code and gives the first
   * five away. So the difference between paying nothing and paying several
   * hundred dollars a year is entirely how concentrated the book is, and that
   * is not a question anybody can answer by looking at a list of phone numbers.
   * This panel answers it: every area code in the book, biggest first, with the
   * five that should take the free allocation marked.
   *
   * It is also where a downloaded file gets loaded, because a screen that tells
   * an agency what to buy and then cannot accept what they bought is half a
   * feature.
   */
  /**
   * Where the book is, and where a machine may lawfully speak.
   *
   * Area codes answer what the registry costs. States answer something else and
   * far more expensive: whether an AI voice may solicit there at all. Michigan
   * prohibits it outright and every unresearched state refuses by default, so
   * without this the question "where can the AI dialer run" is a legal bill per
   * state with no way to know which states are worth the bill.
   *
   * Sorted biggest first, because that is the order worth paying for: clearing
   * the top two or three states is a much smaller question than clearing fifty,
   * and this screen says exactly how much of the book each one buys.
   */
  function whereTheBookIs(status: RegistryStatus): HTMLElement {
    if (!status.byState.length) {
      return panel('Where your leads are', empty('No leads with a state yet',
        'Once a lead file is imported this shows which states your book is in, and whether '
        + 'an AI voice may lawfully call them.'));
    }

    const total = status.byState.reduce((n, r) => n + r.leads, 0);
    const pct = (n: number): string => `${Math.round((n / total) * 100)}%`;
    const openable = status.byState.filter((r) => r.artificialVoice === 'unclear');
    const topThree = openable.slice(0, 3);
    const couldUnlock = topThree.reduce((n, r) => n + r.leads, 0);

    const label: Record<string, [string, string]> = {
      permitted: ['Yes', 'good'],
      prohibited: ['No — this state prohibits it', 'crit'],
      unclear: ['Not checked yet', 'warn'],
    };
    // A state somebody has actually researched is a different thing from one
    // nobody has opened, even though both refuse. Saying so is the difference
    // between "fifty states to investigate" and "two phone calls".
    const statusOf = (r: RegistryStatus['byState'][number]): [string, string] =>
      r.artificialVoice === 'unclear' && r.voiceNote
        ? ['Looks open — needs sign-off', 'cop']
        : (label[r.artificialVoice] ?? ['Not checked yet', 'warn']);

    return panel('Where your leads are, and where an AI voice may call them',
      el('div', { class: 'note', style: 'margin-bottom:14px' },
        el('b', null, 'A human agent may call every state on this list. '),
        'This column is only about an AI voice, which is a separate question a lawyer '
        + 'answers one state at a time. Nothing is marked yes until they do — a state nobody '
        + 'has checked is not a state that came back clear.'),
      table(['State', 'Leads', 'Share of book', 'AI voice'], status.byState.map((r) => {
        const [text, tone] = statusOf(r);
        return {
          cells: [
            r.voiceNote
              ? el('div', null, el('div', { text: r.name }),
                el('div', { class: 'note', style: 'margin-top:4px;max-width:52ch', text: r.voiceNote }))
              : r.name,
            mono(r.leads.toLocaleString()), pct(r.leads), chip(text, tone),
          ],
        };
      })),
      topThree.length
        ? el('div', { class: 'okbox', style: 'margin-top:14px' },
          el('b', null, `Clearing ${topThree.map((r) => r.name).join(', ')} would cover ${pct(couldUnlock)} of your book. `),
          'That is the cheapest version of this question — a few states rather than fifty.')
        : null,
      status.statelessLeads
        ? el('div', { class: 'note', style: 'margin-top:10px' },
          `${status.statelessLeads.toLocaleString()} lead${status.statelessLeads === 1 ? ' has' : 's have'} `
          + 'no state recorded, so neither the calling hours nor this question can be answered for them.')
        : null);
  }

  async function registry(host: HTMLElement): Promise<void> {
    clear(host);
    host.append(skeleton(4));
    let s: RegistryStatus | null = null;
    try { s = await get<RegistryStatus>('/v1/compliance/registry'); } catch { s = null; }
    clear(host);
    if (!s) {
      host.append(panel('Do-not-call registry files',
        el('div', { class: 'warnbox' }, 'Could not load the registry status. ',
          button('Try again', '', () => { void registry(host); }))));
      return;
    }
    const status = s;

    host.append(whereTheBookIs(status));

    const FREE = 5;
    const chargeable = Math.max(0, status.book.length - FREE);
    const covered = status.book.filter((b) => b.coverage === 'fresh').length;

    const head = el('div', { class: chargeable > 0 ? 'note' : 'okbox', style: 'margin-bottom:14px' });
    if (status.book.length === 0) {
      head.append(el('b', null, 'No leads with a usable number yet. '),
        'Import a lead file and this becomes the list of registry files to download.');
    } else {
      head.append(
        el('b', null, `${status.book.length} area code${status.book.length === 1 ? '' : 's'} in your book. `),
        chargeable === 0
          ? 'The FTC gives the first five away, so every file you need is free — '
            + 'you still have to register for a Subscription Account Number to download them.'
          : `The first five are free; the other ${chargeable} carry the FTC's per-area-code fee. `
            + 'Claim the free five on the area codes with the most leads — they are at the top of this list.');
    }

    const rows: Array<{ cells: (string | Node)[] }> = status.book.map((b, i) => ({
      cells: [
        mono(b.areaCode),
        b.leads.toLocaleString(),
        i < FREE ? chip('Free', 'good') : chip('Paid', 'cop'),
        b.coverage === 'fresh' ? chip('Loaded', 'good')
          : b.coverage === 'stale' ? chip(`Older than ${status.maxAgeDays} days`, 'warn')
            : chip('Never loaded', 'crit'),
        loadButton(b.areaCode, host),
      ],
    }));

    const kids: Node[] = [head];
    if (status.book.length > 0) {
      kids.push(table(['Area code', 'Leads', 'FTC fee', 'Registry', ''], rows));
    }
    if (status.unreadable > 0) {
      kids.push(el('div', { class: 'warnbox', style: 'margin-top:12px' },
        el('b', null, `${status.unreadable.toLocaleString()} lead${status.unreadable === 1 ? ' has' : 's have'} no usable number. `),
        'Those are not in the counts above and cannot be dialled at all.'));
    }
    kids.push(el('div', { class: 'note', style: 'margin-top:14px' },
      el('b', null, 'Until an area code says Loaded, its numbers fail the dial check. '),
      'Not because they are on the list — because nothing has looked. A number nobody '
      + 'checked is not a number that came back clear, and this platform will not pretend it is. ',
      el('a', {
        href: 'https://telemarketing.donotcall.gov/', target: '_blank', rel: 'noopener noreferrer',
      }, 'Register for a SAN and download the files'),
      ' — a fresh download is required at least every ',
      String(status.maxAgeDays), ' days.'));

    host.append(panel('Do-not-call registry files', ...kids));
    if (covered > 0 && covered < status.book.length) {
      // Said out loud, because a half-loaded registry is the state that most
      // looks like a finished one.
      host.append(el('div', { class: 'warnbox', style: 'margin-top:-6px' },
        `${covered} of ${status.book.length} area codes are loaded. The rest cannot be dialled yet.`));
    }
  }

  /** Reads one downloaded registry file and sends it up in pieces. */
  function loadButton(areaCode: string, host: HTMLElement): HTMLElement {
    const picker = input({ type: 'file', accept: '.txt,.csv,text/plain,text/csv', style: 'display:none' });
    const go = button('Load file', '', () => picker.click());
    picker.addEventListener('change', async () => {
      const file = picker.files?.[0];
      if (!file) return;
      go.disabled = true;
      go.textContent = 'Reading…';
      try {
        const text = await file.text();
        // The FTC file is one number per line. Anything else in it is a header
        // or a stray column, and a ten-digit run is the only thing worth taking
        // out of it.
        const found = text.match(/\d{10}/g) ?? [];
        if (found.length === 0) throw new Error('No ten-digit numbers in that file.');
        // 50,000 is the request cap. One area code's file is far larger than
        // that, so it goes up in pieces — the server counts what it holds
        // rather than what any one piece carried.
        const CHUNK = 50_000;
        for (let i = 0; i < found.length; i += CHUNK) {
          go.textContent = `Loading ${Math.min(i + CHUNK, found.length).toLocaleString()} of ${found.length.toLocaleString()}…`;
          await post('/v1/compliance/registry', { areaCode, phones: found.slice(i, i + CHUNK) });
        }
        toast(`Loaded ${found.length.toLocaleString()} numbers for ${areaCode}.`, 'ok');
        void registry(host);
      } catch (e) {
        toast(e instanceof ApiError || e instanceof Error ? e.message : 'Could not load that file.', 'crit');
        go.disabled = false;
        go.textContent = 'Load file';
      }
    });
    return el('div', null, go, picker);
  }

  /* ------------------------------------------------------------- scoring -- */
  async function scoring(): Promise<void> {
    clear(body);
    body.append(skeleton(6));
    const cfg = await get<Config>('/v1/config').catch(() => null);
    clear(body);
    if (!cfg) { body.append(el('div', { class: 'warnbox' }, 'Could not load the model.')); return; }
    const model = cfg.scoring;

    const base = input({ type: 'number', value: String(model.base), min: '0', max: '100' });
    const rows = el('div', { class: 'rows' });
    for (const r of model.rules) {
      const pts = input({ type: 'number', value: String(r.points), min: '-50', max: '50', style: 'width:76px;text-align:center' });
      const on = el('input', { type: 'checkbox', checked: r.enabled ? 'true' : null });
      pts.addEventListener('change', () => { r.points = Number(pts.value); });
      on.addEventListener('change', () => { r.enabled = (on as HTMLInputElement).checked; });
      rows.append(row({
        title: r.label,
        sub: r.kind,
        right: el('div', { style: 'display:flex;gap:10px;align-items:center' }, pts, on),
      }));
    }
    const save = button('Save and re-score the book', 'pri');
    save.addEventListener('click', async () => {
      save.disabled = true;
      save.textContent = 'Re-scoring…';
      try {
        const res = await post<{ rescored: number }>('/v1/config/scoring', { ...model, base: Number(base.value) });
        toast(`Saved. ${res.rescored.toLocaleString()} leads re-scored.`, 'ok');
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not save the model.', 'crit'); } finally {
        save.disabled = false;
        save.textContent = 'Save and re-score the book';
      }
    });

    body.append(panel('Lead scoring',
      el('div', { class: 'note', style: 'margin-bottom:14px' },
        el('b', null, 'Change a number and every queue re-sorts. '),
        'A lead\'s score is the base plus every rule that fires, and each lead shows exactly which rules those were.'),
      field('Base score', base),
      rows,
      el('div', { style: 'margin-top:14px' }, save)));
  }

  /* ------------------------------------------------------------- sources -- */
  async function sourcesTab(): Promise<void> {
    clear(body);
    const list = el('div');
    const name = input({ placeholder: 'BlueRock Media' });
    const kind = select([
      ['exclusive', 'Exclusive — sold only to you'],
      ['shared', 'Shared — resold to other agencies'],
      ['referral', 'Referral'],
      ['aged', 'Aged list'],
      ['owned', 'Your own marketing'],
    ], 'exclusive');
    const grade = select([['', 'Not graded'], ['A', 'A — full provenance'], ['B', 'B — partial'], ['C', 'C — thin']], '');
    const cpl = input({ type: 'number', min: '0', step: '0.01', placeholder: '11.00' });
    const warranty = el('input', { type: 'checkbox' });
    const add = button('Add source', 'pri');

    const load = async (): Promise<void> => {
      clear(list);
      const items = await get<Array<{ id: string; name: string; kind: string; costPerLeadCents: number; consentGrade: string | null; consentWarranty: boolean }>>('/v1/sources').catch(() => []);
      if (items.length === 0) {
        list.append(empty('No sources yet', 'Recording where a list came from is what makes cost per issued policy answerable later.'));
        return;
      }
      list.append(table(['Source', 'Type', 'Cost per lead', 'Consent grade', 'Warranty'], items.map((s) => ({
        cells: [
          s.name, s.kind,
          mono(`$${(s.costPerLeadCents / 100).toFixed(2)}`),
          s.consentGrade ?? '—',
          s.consentWarranty ? chip('yes', 'good') : chip('none', 'warn'),
        ],
      }))));
    };

    add.addEventListener('click', async () => {
      if (!name.value.trim()) { toast('Give the source a name.', 'crit'); return; }
      add.disabled = true;
      try {
        await post('/v1/sources', {
          name: name.value.trim(),
          kind: kind.value,
          consentGrade: grade.value || undefined,
          costPerLeadCents: Math.round(Number(cpl.value || 0) * 100),
          consentWarranty: (warranty as HTMLInputElement).checked,
        });
        toast('Source added.', 'ok');
        name.value = ''; cpl.value = '';
        await load();
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not add it.', 'crit'); } finally { add.disabled = false; }
    });

    body.append(panel('Lead sources', list));
    body.append(panel('Add a source',
      field('Name', name),
      field('Type', kind),
      el('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:10px' },
        field('Consent grade', grade, 'How complete the provenance is.'),
        field('Cost per lead', cpl)),
      el('label', { class: 'field' }, el('span', { text: 'Contractual consent warranty' }),
        el('div', { style: 'display:flex;gap:9px;align-items:center;height:38px' }, warranty,
          el('span', { style: 'font-size:12.5px;color:var(--ink-3)', text: 'Does the vendor warrant consent in writing? A cheap lead without one is not cheap.' }))),
      add));
    void load();
  }

  /* -------------------------------------------------------------- people -- */
  /**
   * Changes what one person can do, in place.
   *
   * Their own row shows plain text instead: nobody may change their own role,
   * because that one edit is both a way to promote yourself and a way to lock
   * yourself out of your own agency. The server refuses it as well — this only
   * saves somebody the round trip.
   */
  // `refreshPeople`, not `reload`: this redraws the people table, and the
  // outer `reload` drops the whole cached account. Two different jobs, so
  // they do not get to share a name inside the same file.
  function rolePicker(userId: string, current: string, refreshPeople: () => Promise<void>): HTMLElement {
    // Labels come from the one place that names roles, so this control cannot
    // call somebody an "Owner" while the onboarding form calls them something
    // else. "Agency owner" is the point: it is ownership of Loyalty Life, not
    // of Cellvora.
    const sel = select(
      ORG_ROLES.map((r) => [r, `${ORG_ROLE_LABEL[r]} — ${ORG_ROLE_BLURB[r]}`] as [string, string]),
      current,
    );
    sel.style.fontSize = '12.5px';
    sel.style.padding = '4px 6px';

    sel.addEventListener('change', async () => {
      const next = sel.value;
      if (next === current) return;
      sel.disabled = true;
      try {
        await patch(`/v1/users/${encodeURIComponent(userId)}`, { role: next });
        toast('Role changed.', 'ok');
        await refreshPeople();
      } catch (e) {
        // Put it back to what it was, so the screen never shows a role the
        // server did not accept.
        sel.value = current;
        toast(e instanceof ApiError ? e.message : 'Could not change that role.', 'crit');
      } finally {
        sel.disabled = false;
      }
    });
    return sel;
  }

  /**
   * A link everybody at the agency can join through.
   *
   * Inviting twenty people one address at a time is not a workflow, it is a
   * punishment — and with no email sender, every one of those invitations has
   * to be copied and pasted by hand anyway. One link in the agency's own group
   * chat does the whole floor.
   *
   * What makes that safe is stated on the screen rather than buried, because
   * the person pressing the button is the one who needs to understand it:
   * everybody who joins arrives as an agent seeing only their own leads, and
   * somebody accountable promotes them afterwards.
   */
  function joinLink(): HTMLElement {
    const wrap = el('div');
    const out = el('div', { style: 'margin-top:14px' });

    const days = select([
      ['1', 'Tomorrow'], ['7', 'A week from now'], ['30', 'A month from now'],
    ], '7');
    const cap = select([
      ['', 'No limit'], ['5', '5 people'], ['10', '10 people'],
      ['25', '25 people'], ['50', '50 people'],
    ], '');
    const make = button('Make a join link', 'pri');

    const renderExisting = async (): Promise<void> => {
      const invites = await get<Array<{
        id: string; email: string | null; openLink: boolean;
        maxUses: number | null; useCount: number; expiresAt: string;
      }>>('/v1/invites').catch(() => []);
      const open = invites.filter((i) => i.openLink);
      clear(out);
      if (!open.length) return;

      out.append(el('div', { style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:8px', text: 'Links currently working:' }));
      for (const i of open) {
        const url = `${location.origin}/#/invite?token=…`;
        void url;
        const used = i.maxUses === null
          ? `${i.useCount} joined`
          : `${i.useCount} of ${i.maxUses} used`;
        out.append(row({
          title: used,
          sub: `Works until ${new Date(i.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`,
          right: button('Revoke', 'sm danger', async () => {
            if (!confirm('Revoke this link? Anyone still holding it will not be able to join.')) return;
            try {
              await post(`/v1/invites/${encodeURIComponent(i.id)}/revoke`);
              toast('Revoked. That link no longer works.', 'ok');
              await renderExisting();
            } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not revoke it.', 'crit'); }
          }),
        }));
      }
    };

    make.addEventListener('click', async () => {
      make.disabled = true;
      try {
        const res = await post<{ token: string; expiresAt: string; maxUses: number | null }>(
          '/v1/invites/join-link',
          { days: Number(days.value), ...(cap.value ? { maxUses: Number(cap.value) } : {}) },
        );
        const url = `${location.origin}/#/invite?token=${encodeURIComponent(res.token)}`;
        const box = input({ value: url, readonly: true, spellcheck: 'false' });
        box.style.fontFamily = 'var(--f-mono)';
        box.style.fontSize = '12px';
        box.addEventListener('focus', () => box.select());

        clear(out);
        out.append(
          el('div', { class: 'okbox', style: 'margin-bottom:12px' },
            el('b', null, 'Send this to everyone at the agency. '),
            `It works until ${new Date(res.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
            + `${res.maxUses ? `, for up to ${res.maxUses} people` : ''}. `
            + 'Everyone who joins arrives as an agent — change anybody who needs more above.'),
          box,
          el('div', { style: 'margin-top:10px;display:flex;gap:8px;flex-wrap:wrap' },
            button('Copy link', '', async () => {
              try {
                await navigator.clipboard.writeText(url);
                toast('Copied.', 'ok');
              } catch {
                box.focus(); box.select();
                toast('Copying was blocked — the link is selected, press Ctrl+C (⌘C on a Mac).', 'info');
              }
            })));
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not make a link.', 'crit');
      } finally { make.disabled = false; }
    });

    wrap.append(
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:6px',
        text: 'Instead of inviting people one at a time, make one link and post it '
          + 'wherever your agency talks. Whoever opens it sets their own password '
          + 'and is in.',
      }),
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:14px',
        text: 'Everyone who joins this way becomes an agent, seeing only their own '
          + 'leads. Nobody can become a manager or an administrator through a link — '
          + 'you promote them above, deliberately. Anyone who finds the link can use '
          + 'it, so set an expiry you are comfortable with.',
      }),
      el('div', { style: 'display:flex;gap:10px;flex-wrap:wrap;align-items:flex-end;margin-bottom:12px' },
        field('Works until', days),
        field('How many people', cap)),
      make, out);
    void renderExisting();
    return wrap;
  }

  /**
   * Somebody's licences, opened from their row.
   *
   * WHY THIS IS HERE AND NOT ON A PROFILE PAGE SOMEWHERE
   * A licence is the thing that decides which leads a person may legally be
   * worked. It was self-service only, which sounds reasonable until you watch
   * it fail: a producer earns a new state on Tuesday, nobody tells them the
   * system needs to know, and leads there quietly route to somebody else for a
   * month with nothing reporting a problem. The person who keeps this straight
   * is whoever manages the agency, so it lives where they already are.
   */
  function licenceCell(userId: string, personName: string): HTMLElement {
    const wrap = el('div');
    const summary = el('button', {
      type: 'button',
      class: 'linkish',
      style: 'background:none;border:0;padding:0;color:var(--aqua);cursor:pointer;font:inherit;text-align:left',
    }, 'loading…');
    wrap.append(summary);

    const refresh = async (): Promise<string[]> => {
      const licences = await get<Array<{ state: string }>>(
        `/v1/users/${encodeURIComponent(userId)}/licences`,
      ).catch(() => []);
      const states = licences.map((l) => l.state);
      summary.textContent = states.length ? states.join(', ') : 'None — add';
      return states;
    };
    void refresh();

    summary.addEventListener('click', () => { void openLicences(userId, personName, refresh); });
    return wrap;
  }

  /** The drawer where licences are actually added and removed. */
  async function openLicences(
    userId: string, personName: string, onChange: () => Promise<string[]>,
  ): Promise<void> {
    const body2 = el('div');
    const listing = el('div');

    const load = async (): Promise<void> => {
      clear(listing);
      const licences = await get<Array<{
        id: string; state: string; licenseNumber: string | null;
        expiresAt: string | null; status: string;
      }>>(`/v1/users/${encodeURIComponent(userId)}/licences`).catch(() => []);

      if (!licences.length) {
        listing.append(el('div', { class: 'note' },
          'No licences on file. Until one is added, this person cannot be routed a '
          + 'lead in any state — the rules will find nobody licensed and hold it.'));
        return;
      }
      for (const l of licences) {
        const state = STATES.find((x) => x.code === l.state);
        const expiry = l.expiresAt ? new Date(l.expiresAt) : null;
        const lapsed = expiry !== null && expiry.getTime() < Date.now();
        listing.append(row({
          title: state ? state.name : l.state,
          sub: [
            l.licenseNumber ? `No. ${l.licenseNumber}` : 'No licence number on file',
            expiry
              ? `${lapsed ? 'Expired' : 'Expires'} ${expiry.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
              : 'No expiry date — nothing can warn you before it lapses',
          ].join(' · '),
          right: [
            lapsed ? chip('lapsed', 'crit') : chip('active', 'good'),
            button('Remove', 'sm ghost', async () => {
              if (!confirm(`Remove the ${state ? state.name : l.state} licence?\n\nThey will stop being routed leads in that state.`)) return;
              try {
                await del(`/v1/onboarding/licences/${encodeURIComponent(l.id)}`);
                toast('Removed.', 'ok');
                await load(); await onChange();
              } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not remove it.', 'crit'); }
            }),
          ],
        }));
      }
    };

    const st = select(PLACE_OPTIONS, 'FL');
    const num = input({ placeholder: 'Licence number' });
    const exp = input({ type: 'date' });
    const add = button('Add licence', 'pri');

    add.addEventListener('click', async () => {
      add.disabled = true;
      try {
        await post('/v1/onboarding/licences', {
          forUserId: userId,
          state: st.value,
          licenseNumber: num.value || undefined,
          expiresAt: exp.value || undefined,
        });
        toast('Licence added.', 'ok');
        num.value = ''; exp.value = '';
        await load(); await onChange();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not add that licence.', 'crit');
      } finally { add.disabled = false; }
    });

    body2.append(
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:14px',
        text: 'A licence decides which states this person may be routed leads in. '
          + 'Check the number against the certificate — a wrong digit stops them '
          + 'getting work in that state and nothing reports it.',
      }),
      listing,
      el('div', { style: 'margin-top:20px;padding-top:16px;border-top:1px solid var(--rim)' },
        el('div', { class: 'row3' },
          field('State', st), field('Licence number', num), field('Expires', exp)),
        add),
    );
    drawer(`${personName}'s licences`, body2);
    await load();
  }

  async function people(): Promise<void> {
    clear(body);
    const list = el('div');
    const email = input({ type: 'email', placeholder: 'agent@youragency.com' });
    // No agency owner here on purpose: an invitation link can never mint one.
    // Invite them, then change the role on their row above.
    const role = select(
      ORG_ROLES.filter((r) => r !== 'owner')
        .map((r) => [r, `${ORG_ROLE_LABEL[r]} — ${ORG_ROLE_BLURB[r]}`] as [string, string]),
      'agent',
    );
    // "Create", not "Send". Nothing is emailed — this makes a link, and a
    // person has to pass it on. A button that says Send promises a message that
    // never arrives, and whoever pressed it goes off to wait for a reply to
    // something nobody received.
    const send = button('Create invitation', 'pri');
    const out = el('div');

    send.addEventListener('click', async () => {
      send.disabled = true;
      try {
        const res = await post<{ token: string; invite: { email: string } }>('/v1/invites', { email: email.value, role: role.value });
        const link = `${location.origin}/#/invite?token=${encodeURIComponent(res.token)}`;
        clear(out);
        out.append(el('div', { class: 'okbox' },
          el('b', null, `Invitation created for ${res.invite.email}. `),
          'Send them this link — it works once and expires in fourteen days.'),
          el('div', { class: 'mono', style: 'margin-top:9px;padding:10px;background:var(--hull-3);border:1px solid var(--rim);border-radius:var(--r-sm);word-break:break-all;font-size:11.5px', text: link }),
          el('div', { style: 'margin-top:8px' }, button('Copy link', 'sm', () => {
            void navigator.clipboard?.writeText(link);
            toast('Copied.', 'ok');
          })));
        email.value = '';
        await load();
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not send that invitation.', 'crit'); } finally { send.disabled = false; }
    });

    const load = async (): Promise<void> => {
      clear(list);
      const users = await get<Array<{ id: string; firstName: string; lastName: string; email: string; role: string; status: string; state: string | null }>>('/v1/users').catch(() => []);
      const canAssign = me.capabilities.includes('role.assign');

      list.append(table(['Name', 'Email', 'Role', 'Licences', 'Status'], users.map((u) => ({
        cells: [
          `${u.firstName} ${u.lastName}`,
          u.email,
          // Editable in place rather than behind a dialog. Getting somebody's
          // role wrong is the single easiest mistake to make here — the
          // invitation form defaults to "agent" — so correcting it should be
          // the work of one click, not a hunt.
          canAssign && u.id !== me.user.id
            ? rolePicker(u.id, u.role, load)
            : (ORG_ROLE_LABEL[u.role as OrgRole] ?? u.role),
          // Licences decide which leads a person may legally be routed, so they
          // belong in the list rather than two clicks away. `state` used to sit
          // here, which told nobody anything they needed.
          licenceCell(u.id, `${u.firstName} ${u.lastName}`),
          u.status === 'active' ? chip('active', 'good') : chip(u.status, 'warn'),
        ],
      }))));
    };

    body.append(panel('People in this agency', list));
    body.append(panel('One link for the whole agency', joinLink()));
    body.append(panel('Invite somebody',
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:14px',
        text: 'This makes a link, it does not send an email. Copy it and pass it '
          + 'to them yourself. They open it, choose their own password, and they '
          + 'are in this agency — they never create an account of their own.',
      }),
      field('Email', email), field('Role', role), send, out));
    void load();
  }

  /* ------------------------------------------------------------------ ai -- */
  /**
   * Which houses can answer, and which are dark.
   *
   * Shown because an agency paying three subscriptions has no other way to
   * find out that one of them stopped working. A key that expires does not
   * announce itself: the feature keeps answering, quietly, from whichever
   * provider is next in the list, and the bill moves without anybody deciding
   * that it should.
   */
  function providerRow(live: string[]): HTMLElement {
    const ALL: Array<[string, string]> = [
      ['anthropic', 'Claude'],
      ['openai', 'ChatGPT'],
      ['xai', 'Grok'],
    ];
    const wrap = el('div', { style: 'margin-top:14px' },
      el('div', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:8px',
        text: live.length > 1
          ? 'Features try these in order and fall back automatically, so one provider going down does not take the assistant offline.'
          : 'Add a second provider and features fall back automatically when one is unreachable.',
      }));
    const row = el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' });
    for (const [key, label] of ALL) {
      const on = live.includes(key);
      row.append(chip(on ? `${label} — connected` : `${label} — no key`, on ? 'good' : ''));
    }
    wrap.append(row);
    return wrap;
  }

  async function ai(): Promise<void> {
    clear(body);
    body.append(skeleton(3));
    const s = await get<{
      enabled: boolean; configured: boolean; providers?: string[];
      spendUsd: number; budgetUsd: number; remainingUsd: number;
    }>('/v1/ai/status').catch(() => null);
    clear(body);
    if (!s) { body.append(el('div', { class: 'warnbox' }, 'Could not read the assistant status.')); return; }
    body.append(panel('The assistant',
      el('div', { class: s.enabled ? 'okbox' : 'note' },
        el('b', null, s.enabled ? 'On. ' : s.configured ? 'Switched off for this agency. ' : 'Not configured. '),
        s.configured
          ? 'Every AI feature runs through one service, so spend is attributable and the whole layer can be switched off without changing how the platform behaves.'
          : 'Set at least one of ANTHROPIC_API_KEY, OPENAI_API_KEY or XAI_API_KEY on the server to enable it.'),
      providerRow(s.providers ?? []),
      el('div', { class: 'tiles', style: 'margin-top:12px' },
        el('div', { class: 'tile', style: '--accent:var(--iris)' },
          el('div', { class: 'k', text: 'Spent this month' }), el('div', { class: 'v', text: `$${s.spendUsd.toFixed(2)}` })),
        el('div', { class: 'tile', style: '--accent:var(--aqua)' },
          el('div', { class: 'k', text: 'Budget' }), el('div', { class: 'v', text: `$${s.budgetUsd.toFixed(0)}` })),
        el('div', { class: 'tile', style: '--accent:var(--good)' },
          el('div', { class: 'k', text: 'Remaining' }), el('div', { class: 'v', text: `$${s.remainingUsd.toFixed(2)}` }))),
      el('div', { class: 'note', style: 'margin-top:12px' },
        el('b', null, 'What it can never do. '),
        'The assistant cannot place a call, unblock a record, bypass a do-not-call request or override licensing. It produces suggestions; the deterministic rules decide.')));

    if (me.capabilities.includes('org.manage')) body.append(panel('Test the connection', selfTest()));
  }

  /**
   * Proves the assistant works, by using it.
   *
   * A green chip above only means a key is present. It cannot tell you whether
   * the key is valid, whether there is credit on the account, or whether the
   * model identifier still exists — and a model identifier is somebody else's
   * string that changes on their schedule. When one goes stale the platform
   * quietly falls through to the next provider and every feature keeps working,
   * which is exactly why nobody notices for weeks.
   *
   * So: press the button, send a real request, read the provider's own answer.
   */
  function selfTest(): HTMLElement {
    const wrap = el('div');
    const out = el('div', { style: 'margin-top:14px' });
    const run = button('Test all providers', 'pri');

    run.addEventListener('click', async () => {
      run.disabled = true;
      run.textContent = 'Testing…';
      clear(out);
      try {
        const res = await post<{
          results: Array<{
            label: string; model: string; ok: boolean;
            latencyMs: number; reply?: string; error?: string;
          }>;
        }>('/v1/ai/selftest');

        if (!res.results.length) {
          out.append(el('div', { class: 'warnbox' },
            'No provider has a key, so there was nothing to test.'));
          return;
        }

        const good = res.results.filter((r) => r.ok).length;
        out.append(el('div', { class: good === res.results.length ? 'okbox' : 'warnbox' },
          el('b', null, `${good} of ${res.results.length} answered. `),
          good === res.results.length
            ? 'Every configured provider is reachable and its model exists.'
            : 'Details below — the message is the provider\'s own.'));

        out.append(table(['Provider', 'Model', 'Result', 'Took'], res.results.map((r) => ({
          cells: [
            r.label,
            el('span', { class: 'mono', style: 'font-size:11.5px', text: r.model }),
            r.ok
              ? chip('answered', 'good')
              : el('span', null, chip('failed', 'crit'), ' ',
                el('span', { style: 'font-size:12px', text: r.error ?? '' })),
            `${r.latencyMs} ms`,
          ],
        }))));
      } catch (e) {
        out.append(el('div', { class: 'warnbox' },
          e instanceof ApiError ? e.message : 'Could not run the test.'));
      } finally {
        run.disabled = false;
        run.textContent = 'Test all providers';
      }
    });

    wrap.append(
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:12px',
        text: 'Sends one real request to each provider that has a key and shows '
          + 'exactly what came back. Costs a fraction of a cent. This is the only '
          + 'check that proves the assistant works rather than merely that it is '
          + 'configured.',
      }),
      run, out);
    return wrap;
  }

  /* --------------------------------------------------------------- audit -- */
  async function audit(): Promise<void> {
    clear(body);
    body.append(skeleton(6));
    const [entries, verify] = await Promise.all([
      get<Array<{ id: number; action: string; entity: string | null; entityId: string | null; actorLabel: string | null; occurredAt: string; hash: string }>>('/v1/audit?limit=200').catch(() => []),
      get<{ ok: boolean; length: number; brokenAt: number | null; reason: string | null }>('/v1/audit/verify').catch(() => null),
    ]);
    clear(body);
    body.append(panel('Integrity',
      verify
        ? el('div', { class: verify.ok ? 'okbox' : 'warnbox' },
          el('b', null, verify.ok ? `Chain intact across ${verify.length.toLocaleString()} entries. ` : 'The chain is broken. '),
          verify.ok
            ? 'Every entry carries the hash of the one before it, and each hash still matches its own contents.'
            : `${verify.reason} First problem at entry ${verify.brokenAt}.`)
        : el('div', { class: 'warnbox' }, 'Could not verify the chain.')));
    body.append(panel('Recent activity',
      table(['When', 'Who', 'Action', 'Record', 'Hash'], entries.map((e) => ({
        cells: [
          new Date(e.occurredAt).toLocaleString(),
          e.actorLabel ?? 'system',
          e.action,
          el('span', { class: 'mono', style: 'font-size:11px', text: `${e.entity ?? ''} ${e.entityId ?? ''}` }),
          el('span', { class: 'mono', style: 'font-size:10px;color:var(--ink-4)', text: e.hash.slice(0, 12) }),
        ],
      })))));
  }

  void chip;
  return host;
}
